/**
 * 앱에 실려 나가는 오픈소스 목록 — 오픈소스 라이선스 화면(`LicensesPage`)의 데이터.
 *
 * 범위는 **직접 쓰는 런타임 의존성**이다: 웹 번들이 import 하는 패키지와, 네이티브 셸
 * (`apps/mobile`)이 import 하는 패키지·SDK. 개발 도구(vitest·vite)와 전이 의존성은 없다.
 * 저작권 줄은 각 패키지의 `LICENSE` 파일 첫 저작권 줄에서 옮겼다(Async Storage 는 배포
 * 패키지에 파일이 없어 업스트림 저장소의 것). 라이선스 id 는 `licenses.spec.ts` 가 설치된
 * `package.json` 의 `license` 와 대조한다 — 의존성을 올려 라이선스가 바뀌면 스펙이 깨진다.
 * 새 의존성을 들이면 여기에 한 줄 더한다.
 */

export type LicenseId = 'MIT' | 'Apache-2.0' | 'ISC' | 'OFL-1.1';

export interface OssEntry {
  name: string;
  /** npm 패키지 — 스펙이 설치본과 대조한다. 비었으면(CDN 폰트·네이티브 SDK) 대조 밖. */
  packages: readonly string[];
  license: LicenseId;
  copyright: string;
}

/** 화면에 뜨는 라이선스 이름 — SPDX id 는 사람이 읽는 이름이 아니다. */
export const LICENSE_NAMES: Record<LicenseId, string> = {
  MIT: 'MIT License',
  'Apache-2.0': 'Apache License 2.0',
  ISC: 'ISC License',
  'OFL-1.1': 'SIL Open Font License 1.1',
};

export const OSS_ENTRIES: readonly OssEntry[] = [
  {
    name: 'React',
    packages: ['react', 'react-dom'],
    license: 'MIT',
    copyright: 'Meta Platforms, Inc. and affiliates',
  },
  {
    name: 'React Native',
    packages: ['react-native'],
    license: 'MIT',
    copyright: 'Meta Platforms, Inc. and affiliates',
  },
  {
    name: 'React Router',
    packages: ['react-router', 'react-router-dom'],
    license: 'MIT',
    copyright: 'React Training LLC, Remix Software Inc.',
  },
  {
    name: 'React Navigation',
    packages: ['@react-navigation/native', '@react-navigation/native-stack'],
    license: 'MIT',
    copyright: 'React Navigation Contributors',
  },
  {
    name: 'react-native-screens',
    packages: ['react-native-screens'],
    license: 'MIT',
    copyright: 'Software Mansion',
  },
  {
    name: 'react-native-safe-area-context',
    packages: ['react-native-safe-area-context'],
    license: 'MIT',
    copyright: 'Th3rd Wave',
  },
  {
    name: 'React Native WebView',
    packages: ['react-native-webview'],
    license: 'MIT',
    copyright: 'Facebook, Inc.',
  },
  {
    name: 'Async Storage',
    packages: ['@react-native-async-storage/async-storage'],
    license: 'MIT',
    copyright: 'Krzysztof Borowy',
  },
  {
    name: 'NetInfo',
    packages: ['@react-native-community/netinfo'],
    license: 'MIT',
    copyright: 'Facebook, Inc.',
  },
  {
    name: 'react-native-device-info',
    packages: ['react-native-device-info'],
    license: 'MIT',
    copyright: 'Rebecca Hughes',
  },
  {
    name: 'react-native-permissions',
    packages: ['react-native-permissions'],
    license: 'MIT',
    copyright: 'Mathieu Acthernoene',
  },
  {
    name: 'react-native-bootsplash',
    packages: ['react-native-bootsplash'],
    license: 'MIT',
    copyright: 'Mathieu Acthernoene',
  },
  {
    name: 'react-native-haptic-feedback',
    packages: ['react-native-haptic-feedback'],
    license: 'MIT',
    copyright: 'Michael Kuczera',
  },
  {
    name: 'react-native-image-picker',
    packages: ['react-native-image-picker'],
    license: 'MIT',
    copyright: 'Facebook, Inc.',
  },
  {
    name: 'react-native-toast-message',
    packages: ['react-native-toast-message'],
    license: 'MIT',
    copyright: 'Calin Tamas',
  },
  {
    name: 'i18next',
    packages: ['i18next', 'i18next-browser-languagedetector'],
    license: 'MIT',
    copyright: 'i18next',
  },
  {
    name: 'react-i18next',
    packages: ['react-i18next'],
    license: 'MIT',
    copyright: 'i18next',
  },
  {
    name: 'Radix UI',
    packages: ['@radix-ui/react-tabs', '@radix-ui/react-slot'],
    license: 'MIT',
    copyright: 'WorkOS',
  },
  {
    name: 'Sonner',
    packages: ['sonner'],
    license: 'MIT',
    copyright: 'Emil Kowalski',
  },
  {
    name: 'Tailwind CSS',
    packages: ['tailwindcss'],
    license: 'MIT',
    copyright: 'Tailwind Labs, Inc.',
  },
  {
    name: 'tailwind-merge',
    packages: ['tailwind-merge'],
    license: 'MIT',
    copyright: 'Dany Castillo',
  },
  {
    name: 'clsx',
    packages: ['clsx'],
    license: 'MIT',
    copyright: 'Luke Edwards',
  },
  {
    name: 'Firebase SDK',
    packages: [],
    license: 'Apache-2.0',
    copyright: 'Google LLC',
  },
  {
    name: 'React Native Firebase',
    packages: [
      '@react-native-firebase/app',
      '@react-native-firebase/messaging',
    ],
    license: 'Apache-2.0',
    copyright: 'Invertase Limited',
  },
  {
    name: 'Notifee',
    packages: ['@notifee/react-native'],
    license: 'Apache-2.0',
    copyright: 'Invertase Limited',
  },
  {
    name: 'class-variance-authority',
    packages: ['class-variance-authority'],
    license: 'Apache-2.0',
    copyright: 'Joe Bell',
  },
  {
    name: 'Lucide',
    packages: ['lucide-react'],
    license: 'ISC',
    copyright: 'Lucide Contributors, Cole Bemis (Feather)',
  },
  {
    name: 'Pretendard',
    packages: [],
    license: 'OFL-1.1',
    copyright: 'Kil Hyung-jin',
  },
];

export interface LicenseGroup {
  license: LicenseId;
  entries: readonly OssEntry[];
}

/** 라이선스별 묶음 — 첫 등장 순서(MIT → Apache → ISC → OFL). 전문을 묶음마다 한 번만 싣는다. */
export const groupByLicense = (
  entries: readonly OssEntry[],
): LicenseGroup[] => {
  const groups = new Map<LicenseId, OssEntry[]>();
  for (const entry of entries) {
    const group = groups.get(entry.license) ?? [];
    group.push(entry);
    groups.set(entry.license, group);
  }
  return [...groups].map(([license, grouped]) => ({
    license,
    entries: grouped,
  }));
};
